import { useEffect, useRef, useState } from 'react';
import { Moon, Sun } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  useThemeStore,
  THEMES,
  isDarkChromeTheme,
  type ThemeId,
} from '@/entities/session/model/themeStore';
import { cn } from '@/shared/lib/cn';

function themeIcon(id: ThemeId) {
  return isDarkChromeTheme(id) ? Moon : Sun;
}

interface ThemeSwitcherProps {
  compact?: boolean;
}

export function ThemeSwitcher({ compact = false }: ThemeSwitcherProps) {
  const { theme, setTheme } = useThemeStore();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const CurrentIcon = themeIcon(theme);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const pick = (id: ThemeId) => {
    setTheme(id);
    setOpen(false);
  };

  if (compact) {
    return (
      <div ref={rootRef} className="relative">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex h-9 w-9 items-center justify-center rounded-full gradient-emerald-gold text-white shadow-emerald hover-scale"
          title={t(`theme.${theme}.name`)}
          aria-label={t(`theme.${theme}.name`)}
        >
          <CurrentIcon className="h-4 w-4" />
        </button>
        {open && (
          <div className="absolute right-0 top-full z-50 mt-2 w-48 rounded-lg border border-border bg-surface/95 py-1 shadow-lg backdrop-blur-sm">
            {THEMES.map((th) => {
              const Icon = themeIcon(th.id);
              return (
                <button
                  key={th.id}
                  type="button"
                  onClick={() => pick(th.id)}
                  className={cn(
                    'flex w-full items-center gap-3 px-3 py-2.5 text-sm transition-colors hover:bg-surface-tertiary',
                    theme === th.id ? 'font-medium text-text-primary' : 'text-text-secondary',
                  )}
                >
                  <Icon className="h-4 w-4" />
                  <span>{t(`theme.${th.id}.name`)}</span>
                  {theme === th.id && (
                    <span className="ml-auto h-2 w-2 rounded-full bg-ai-primary animate-pulse-glow" />
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  return (
    <div ref={rootRef} className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 rounded-lg border border-border bg-surface px-3 py-2 text-sm font-medium text-text-primary transition-colors hover:bg-surface-tertiary"
      >
        <CurrentIcon className="h-4 w-4 text-ai-primary" />
        {t(`theme.${theme}.name`)}
      </button>
      {open && (
        <div className="absolute left-0 top-full z-50 mt-2 w-56 rounded-lg border border-border bg-surface py-1 shadow-lg">
          {THEMES.map((th) => {
            const Icon = themeIcon(th.id);
            return (
              <button
                key={th.id}
                type="button"
                onClick={() => pick(th.id)}
                className={cn(
                  'flex w-full items-start gap-3 px-3 py-2.5 text-left transition-colors hover:bg-surface-tertiary',
                  theme === th.id && 'bg-surface-tertiary',
                )}
              >
                <Icon className="mt-0.5 h-4 w-4 shrink-0 text-ai-primary" />
                <div>
                  <div className="text-sm font-medium text-text-primary">
                    {t(`theme.${th.id}.name`)}
                  </div>
                  <div className="text-xs text-text-tertiary">
                    {t(`theme.${th.id}.description`)}
                  </div>
                </div>
                {theme === th.id && (
                  <span
                    className="ml-auto mt-1 inline-block h-3 w-3 shrink-0 rounded-full"
                    style={{ background: th.accent }}
                  />
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
